import { action, computed, observable, reaction } from 'mobx';
import { flatMap } from 'lodash';
import ApplicationDefinitionConditionalService from '../services/ApplicationDefinitionConditionalService';
import { AdditionalClassificationDto } from '../types';
import { AdditionalClassificationVerification } from '../types/AdditionalClassificationVerification';
import {
    classificationOccurrenceKeys,
    classificationPath,
    classificationVerificationDefinitions
} from '../misc/classifications';
import { cloneJson } from '../types/TabImportDraft';

type VerificationDraft = {
    projectId: string;
    applicationId: string;
    current: AdditionalClassificationDto[];
};

type ClassificationOccurrence = {
    path: string;
    lineage: AdditionalClassificationDto[];
    typeCodes: string[];
    duplicated: boolean;
};

function classificationOccurrences(
    definitions: AdditionalClassificationDto[],
    parent?: ClassificationOccurrence
): ClassificationOccurrence[] {
    const keys = classificationOccurrenceKeys(definitions);
    return flatMap(definitions, (definition, index) => {
        const occurrence = {
            path: classificationPath(parent?.path, keys[index]),
            lineage: [...(parent?.lineage ?? []), definition],
            typeCodes: [...(parent?.typeCodes ?? []), definition.typeCode],
            duplicated: !!parent?.duplicated || keys[index] !== definition.classificationSlot
        };
        return [occurrence, ...classificationOccurrences(definition.children ?? [], occurrence)];
    });
}

const incomplete: AdditionalClassificationVerification = {
    mismatchedClassificationPaths: [],
    verificationIncomplete: true,
    warnings: []
};

export default class AdditionalClassificationVerificationStore {
    @observable.ref private pending: { snapshot: string } | undefined;
    @observable.ref private completed:
        | {
              snapshot: string;
              projectId: string;
              applicationId: string;
              occurrences: ClassificationOccurrence[];
              result: AdditionalClassificationVerification;
          }
        | undefined;

    constructor(
        private readonly getDraft: () => VerificationDraft | undefined,
        isActive: () => boolean,
        private readonly service: ApplicationDefinitionConditionalService
    ) {
        reaction(
            () => (isActive() ? this.snapshot : undefined),
            snapshot => {
                if (snapshot) {
                    void this.verify();
                }
            }
        );
    }

    @computed private get snapshot(): string | undefined {
        const draft = this.getDraft();
        return draft
            ? JSON.stringify({ ...draft, current: classificationVerificationDefinitions(draft.current) })
            : undefined;
    }

    @computed private get currentResult(): AdditionalClassificationVerification | undefined {
        return this.completed?.snapshot === this.snapshot ? this.completed?.result : undefined;
    }

    @computed get isVerifying(): boolean {
        return !!this.pending;
    }

    @computed get mismatchPaths(): string[] {
        if (this.currentResult) {
            return this.currentResult.mismatchedClassificationPaths;
        }
        const draft = this.getDraft();
        const completed = this.completed;
        if (
            !draft ||
            !completed ||
            this.pending?.snapshot !== this.snapshot ||
            completed.projectId !== draft.projectId ||
            completed.applicationId !== draft.applicationId
        ) {
            return [];
        }
        // Keep established marks while the edited draft is checked. Duplicate occurrence paths
        // may be reused by a different row, so every ancestor must retain its object identity.
        const current = classificationOccurrences(draft.current);
        return completed.result.mismatchedClassificationPaths.filter(path => {
            const previous = completed.occurrences.find(occurrence => occurrence.path === path);
            const next = current.find(occurrence => occurrence.path === path);
            return (
                previous &&
                next &&
                previous.typeCodes.length === next.typeCodes.length &&
                previous.typeCodes.every((typeCode, index) => typeCode === next.typeCodes[index]) &&
                (!(previous.duplicated || next.duplicated) ||
                    previous.lineage.every((row, index) => row === next.lineage[index]))
            );
        });
    }

    @computed get verificationIncomplete(): boolean {
        return !!this.currentResult?.verificationIncomplete;
    }

    @action.bound
    reset() {
        this.pending = undefined;
        this.completed = undefined;
    }

    @action.bound
    async verify(force = false) {
        const draft = this.getDraft();
        const snapshot = this.snapshot;
        if (!draft || !snapshot) {
            return;
        }
        if (this.pending?.snapshot === snapshot || (!force && this.currentResult && !this.verificationIncomplete)) {
            return;
        }
        const occurrences = classificationOccurrences(draft.current);
        const attempt = { snapshot };
        this.pending = attempt;
        try {
            const result = await this.service
                .verifyAdditionalClassifications(draft.projectId, draft.applicationId, cloneJson(draft.current))
                .then(
                    response => (response.isOk() ? response.value : incomplete),
                    () => incomplete
                );
            if (this.pending === attempt && this.snapshot === snapshot) {
                this.completed = {
                    snapshot,
                    projectId: draft.projectId,
                    applicationId: draft.applicationId,
                    occurrences,
                    result
                };
            }
        } finally {
            if (this.pending === attempt) {
                this.pending = undefined;
            }
        }
    }
}
